import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Alert, Spinner, Table, Badge, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import FalconCardHeader from '../common/FalconCardHeader';
import apiClient from '../../services/api';

const ChatConfiguration = () => {
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);

  // Cargar configuraciones al montar el componente
  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Cargando configuraciones de chat...');
      
      const response = await apiClient.get('/chat-config');
      
      console.log('📥 Respuesta del servidor:', response.data);
      
      // El backend devuelve {success: true, data: [...]}
      const responseData = response.data.data || response.data;
      
      if (Array.isArray(responseData)) {
        setConfigurations(responseData);
        console.log(`✅ Configuraciones cargadas: ${responseData.length} items`);
        
        // Si no hay configuraciones, mostrar opción para inicializar
        if (responseData.length === 0) {
          setError('No se encontraron configuraciones. ¿Deseas inicializar las configuraciones por defecto?');
        }
      } else {
        console.error('❌ Respuesta inesperada:', responseData);
        throw new Error('Formato de respuesta inesperado');
      }
    } catch (err) {
      console.error('❌ Error loading configurations:', err);
      setError('Error al cargar las configuraciones del chat');
      toast.error('Error al cargar las configuraciones del chat');
    } finally {
      setLoading(false);
    }
  };

  const updateConfiguration = async (configKey, newValue) => {
    try {
      setSaving(true);
      
      console.log(`🔧 Actualizando configuración: ${configKey} = ${newValue}`);
      
      const response = await apiClient.put(`/chat-config/${configKey}`, {
        config_value: newValue
      });

      console.log('✅ Configuración actualizada:', response.data);

      // Actualizar el estado local
      setConfigurations(prev => 
        prev.map(config => 
          config.config_key === configKey 
            ? { ...config, config_value: newValue }
            : config
        )
      );

      toast.success('Configuración actualizada correctamente');
    } catch (err) {
      console.error('❌ Error updating configuration:', err);
      toast.error('Error al actualizar la configuración');
    } finally {
      setSaving(false);
    }
  };

  // Nuevo método para inicializar configuraciones
  const initializeConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🚀 Inicializando configuraciones por defecto...');
      
      const response = await apiClient.post('/chat-config/initialize');
      
      console.log('✅ Configuraciones inicializadas:', response.data);
      
      toast.success('Configuraciones inicializadas exitosamente');
      
      // Recargar configuraciones
      await loadConfigurations();
    } catch (err) {
      console.error('❌ Error initializing configurations:', err);
      toast.error('Error al inicializar configuraciones');
      setError('Error al inicializar las configuraciones');
    } finally {
      setLoading(false);
    }
  };

  // Método de diagnóstico
  const runDiagnostics = async () => {
    try {
      console.log('🔍 Ejecutando diagnóstico...');
      
      const endpoints = [
        { name: 'GET /chat-config', url: '/chat-config' },
        { name: 'GET /chat/health', url: '/chat/health' },
        { name: 'GET /chat/diagnostics', url: '/chat/diagnostics' }
      ];

      const results = {};
      
      for (const endpoint of endpoints) {
        try {
          const response = await apiClient.get(endpoint.url);
          results[endpoint.name] = {
            success: true,
            status: response.status,
            data: response.data
          };
        } catch (error) {
          results[endpoint.name] = {
            success: false,
            error: error.message,
            status: error.response?.status || 'No Response'
          };
        }
      }

      setDiagnostics({
        timestamp: new Date().toISOString(),
        results,
        userAgent: navigator.userAgent,
        url: window.location.href
      });

      setShowDiagnostics(true);
    } catch (err) {
      console.error('❌ Error running diagnostics:', err);
      toast.error('Error ejecutando diagnóstico');
    }
  };

  const handleInputChange = (configKey, value, configType) => {
    let processedValue = value;
    
    // Procesar según el tipo
    if (configType === 'boolean') {
      processedValue = value === 'true' || value === true;
    } else if (configType === 'number') {
      processedValue = parseInt(value, 10);
    }

    updateConfiguration(configKey, processedValue);
  };

  const renderConfigurationInput = (config) => {
    const { config_key, config_value, config_type, is_active } = config;

    switch (config_type) {
      case 'boolean':
        return (
          <Form.Check
            type="switch"
            id={`switch-${config_key}`}
            checked={config_value === 'true' || config_value === true}
            onChange={(e) => handleInputChange(config_key, e.target.checked, config_type)}
            disabled={saving || !is_active}
          />
        );
      
      case 'number':
        return (
          <Form.Control
            type="number"
            value={config_value}
            onChange={(e) => handleInputChange(config_key, e.target.value, config_type)}
            disabled={saving || !is_active}
            size="sm"
            style={{ maxWidth: '150px' }}
          />
        );
      
      case 'text':
      default:
        return (
          <Form.Control
            type="text"
            value={config_value}
            onChange={(e) => handleInputChange(config_key, e.target.value, config_type)}
            disabled={saving || !is_active}
            size="sm"
          />
        );
    }
  };

  const getConfigKeyDisplayName = (key) => {
    const displayNames = {
      'chat_enabled': 'Chat Habilitado',
      'max_messages_per_session': 'Máximo Mensajes por Sesión',
      'max_message_length': 'Longitud Máxima del Mensaje',
      'ai_provider': 'Proveedor de IA',
      'ai_model': 'Modelo de IA',
      'response_timeout': 'Timeout de Respuesta (ms)',
      'rate_limit_requests': 'Límite de Solicitudes',
      'rate_limit_window': 'Ventana de Límite (ms)',
      'chat_history_enabled': 'Historial Habilitado',
      'chat_admin_only_config': 'Solo Admin Configuración'
    };
    return displayNames[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <Card className="h-100">
        <Card.Body className="d-flex align-items-center justify-content-center">
          <Spinner animation="border" variant="primary" />
          <span className="ms-2">Cargando configuraciones...</span>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <FalconCardHeader 
          title="Configuración del Chat" 
          light={false}
        >
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>
              <FontAwesomeIcon icon="comments" className="me-2" />
              Panel de configuración del sistema de chat
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={runDiagnostics}
              disabled={loading}
            >
              <FontAwesomeIcon icon="stethoscope" className="me-1" />
              Diagnóstico
            </Button>
          </div>
        </FalconCardHeader>

        <Card.Body>
          {error && (
            <Alert variant={error.includes('No se encontraron configuraciones') ? "warning" : "danger"} className="mb-4">
              <FontAwesomeIcon icon={error.includes('No se encontraron configuraciones') ? "info-circle" : "exclamation-triangle"} className="me-2" />
              {error}
              <div className="mt-2">
                <Button 
                  variant={error.includes('No se encontraron configuraciones') ? "outline-warning" : "outline-danger"}
                  size="sm" 
                  className="me-2"
                  onClick={loadConfigurations}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon="sync-alt" className="me-1" />
                  Reintentar
                </Button>
                {error.includes('No se encontraron configuraciones') && (
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={initializeConfigurations}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon="cog" className="me-1" />
                    Inicializar Configuraciones
                  </Button>
                )}
              </div>
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <div className="mb-4">
                <h5 className="mb-3">
                  <FontAwesomeIcon icon="cog" className="me-2 text-primary" />
                  Configuraciones del Sistema
                </h5>
                <p className="text-muted mb-4">
                  Gestiona las configuraciones del sistema de chat. Los cambios se aplican inmediatamente.
                </p>

                {saving && (
                  <Alert variant="info" className="mb-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Guardando configuración...
                  </Alert>
                )}

                <Table hover responsive>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '25%' }}>Configuración</th>
                      <th style={{ width: '15%' }}>Valor</th>
                      <th style={{ width: '10%' }}>Tipo</th>
                      <th style={{ width: '40%' }}>Descripción</th>
                      <th style={{ width: '10%' }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configurations.map((config) => (
                      <tr key={config.config_key}>
                        <td>
                          <strong>{getConfigKeyDisplayName(config.config_key)}</strong>
                          <br />
                          <small className="text-muted">{config.config_key}</small>
                        </td>
                        <td>
                          {renderConfigurationInput(config)}
                        </td>
                        <td>
                          <Badge 
                            bg={config.config_type === 'boolean' ? 'info' : 
                                config.config_type === 'number' ? 'warning' : 'secondary'}
                          >
                            {config.config_type}
                          </Badge>
                        </td>
                        <td>
                          <small>{config.description}</small>
                        </td>
                        <td>
                          <Badge bg={config.is_active ? 'success' : 'secondary'}>
                            {config.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {configurations.length === 0 && !loading && (
                  <Alert variant="info" className="text-center">
                    <FontAwesomeIcon icon="info-circle" className="me-2" />
                    No se encontraron configuraciones del chat.
                  </Alert>
                )}
              </div>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col lg={12}>
              <Card className="border-0 bg-light">
                <Card.Body>
                  <h6 className="mb-3">
                    <FontAwesomeIcon icon="info-circle" className="me-2 text-info" />
                    Información del Sistema
                  </h6>
                  <Row>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Total de configuraciones:</strong> {configurations.length}
                      </p>
                      <p className="mb-2">
                        <strong>Configuraciones activas:</strong> {configurations.filter(c => c.is_active).length}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Última actualización:</strong> {new Date().toLocaleString('es-ES')}
                      </p>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={loadConfigurations}
                          disabled={loading}
                        >
                          <FontAwesomeIcon icon="sync-alt" className="me-1" />
                          Recargar
                        </Button>
                        <Button 
                          variant="outline-warning" 
                          size="sm"
                          onClick={initializeConfigurations}
                          disabled={loading}
                        >
                          <FontAwesomeIcon icon="cog" className="me-1" />
                          Inicializar
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Modal de Diagnóstico */}
      <Modal show={showDiagnostics} onHide={() => setShowDiagnostics(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon="stethoscope" className="me-2" />
            Diagnóstico del Sistema de Chat
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {diagnostics && (
            <div>
              <Alert variant="info" className="mb-3">
                <strong>Ejecutado:</strong> {new Date(diagnostics.timestamp).toLocaleString()}
              </Alert>
              
              <h6>Resultados de Endpoints:</h6>
              <Table size="sm" responsive>
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th>Estado</th>
                    <th>Código</th>
                    <th>Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(diagnostics.results).map(([endpoint, result]) => (
                    <tr key={endpoint}>
                      <td><code>{endpoint}</code></td>
                      <td>
                        <Badge bg={result.success ? 'success' : 'danger'}>
                          {result.success ? 'OK' : 'Error'}
                        </Badge>
                      </td>
                      <td>{result.status}</td>
                      <td>
                        {result.success ? (
                          <small>✅ Respuesta exitosa</small>
                        ) : (
                          <small className="text-danger">❌ {result.error}</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <h6 className="mt-3">Datos de Depuración:</h6>
              <pre className="bg-light p-2" style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                {JSON.stringify(diagnostics, null, 2)}
              </pre>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDiagnostics(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChatConfiguration; 